import { create } from 'zustand';
import type { TeamAccess, TeamPage, TeamSwitches } from '@/api/team';

/**
 * Team access (Ray 2026-10-05): which account this browser is WORKING ON.
 * null = my own account. Picked under "Switch to" in the profile menu; when
 * set, the axios client sends X-Act-As and the server runs every request as
 * that owner — their WHOLE dashboard, limited to the pages they ticked.
 * localStorage is a per-browser convenience only — wrapped in try/catch.
 */
export interface ActingAccount {
   ownerId: string;
   ownerEmail: string;
   /** Shown in the greeting while helping ("Welcome back, kakerou blue"). */
   ownerName?: string;
   access: TeamAccess;
   pages: TeamPage[];
   switches: TeamSwitches;
   /** Opened from the Fleet tab as SmartFi support (admins/staff), not via a share. */
   support?: boolean;
}

/** Is this side-menu page open to me? (Always true on my own account.) */
export const actingHasPage = (a: ActingAccount | null, page: string) =>
   !a || a.access === 'full' || a.pages.includes(page as TeamPage);

/** Can I see money amounts? */
export const actingShowsMoney = (a: ActingAccount | null) =>
   !a || a.access === 'full' || a.switches.showMoney;

const KEY = 'smartfi-acting-as';
export const PENDING_INVITE_KEY = 'smartfi-team-invite';

const read = (): ActingAccount | null => {
   try {
      const raw = window.localStorage.getItem(KEY);
      const v = raw ? JSON.parse(raw) : null;
      if (!v || typeof v.ownerId !== 'string') return null;
      // Saved by an older build (groups/scope): drop it — the server is the
      // truth and the profile menu re-lists the account.
      if (!Array.isArray(v.pages) || !v.switches || typeof v.ownerEmail !== 'string') return null;
      return v as ActingAccount;
   } catch {
      return null;
   }
};

interface State {
   acting: ActingAccount | null;
   setActing: (a: ActingAccount | null) => void;
}

export const useTeamStore = create<State>((set) => ({
   acting: typeof window === 'undefined' ? null : read(),
   setActing: (a) => {
      try {
         if (a) window.localStorage.setItem(KEY, JSON.stringify(a));
         else window.localStorage.removeItem(KEY);
      } catch {
         /* storage blocked — still works for this tab */
      }
      set({ acting: a });
   },
}));

/** Requests that are always MY OWN account, even while helping someone:
 *  sign-in, my invites/accounts, and my OWN partner / credit standing (the
 *  layout asks for these on every page — sent as the owner they were refused
 *  and retried every 2 s, Ray's prod console 2026-10-18). */
export const isOwnAccountCall = (url: string) =>
   /^\/?(auth\/|team\/workspaces|team\/accept|team\/invites|vendo\/(reseller\/me|distributor-applications\/mine|points\/me|referral\/me|redemptions\/me|credit-packages\/mine)$)/.test(
      url.replace(/^https?:\/\/[^/]+\/api/, '').replace(/^\/api/, '')
   );

/**
 * Switching accounts reloads into /wifi so nothing from the other account
 * stays on screen. The old page used to sit there, frozen-looking, until the
 * new one arrived (slow line → "it loads forever", Ray 2026-10-18); now a
 * cover says what is happening until the new page replaces it.
 */
export function reloadIntoAccount(label: string) {
   try {
      const el = document.createElement('div');
      el.setAttribute('role', 'status');
      el.style.cssText =
         'position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;' +
         'background:rgba(15,18,30,.78);color:#fff;font:600 15px/1.4 system-ui,sans-serif;text-align:center;padding:24px';
      el.textContent = `Switching to ${label}…`;
      document.body.appendChild(el);
   } catch {
      /* no DOM — just go */
   }
   window.location.assign('/wifi');
}
